<template>
  <q-dialog v-model="previewDialog">
    <q-card
      id="previewCard"
      class="resultsModal"
      style="min-width: 100%; min-height: 100%; border-radius: 10px; overflow-x: hidden;"
    >
      <q-card-section align="center">
        <div style="float: right"><q-btn icon="close" flat round dense v-close-popup class="circle-btn text-primary" style="margin-bottom: 0px" /></div>
        <div class="text-h6" style="font-weight: bold">{{ modalTitle }}</div>
        <div v-if="fileLoading">This window can be closed and the file will continue downloading in the background.</div>
      </q-card-section>

      <q-card-section class="q-pt-none" align="center">
        <div class="spinner" v-if="fileLoading">
          <div class="loading-text" style="line-height: 22px">
            <img src="fd-logo.svg">
          </div>
          <div class="spinner-item"></div>
          <div class="spinner-item"></div>
        </div>

        <q-btn-dropdown
          color="primary"
          label="Sheets"
          style="margin-bottom: 25px"
          no-caps
          v-if="isExcel"
        >
          <q-list v-for="(sheetName, idx) in sheetListArray" :key="idx">
            <q-item
              clickable
              v-close-popup
              @click="generateTableFromSheet(sheetName)"
            >
              <q-item-section>
                <q-item-label>{{ sheetName }}</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>

        <iframe
          :src="previewSourceURL"
          width="100%"
          :height="heightModal"
          :style="styleModal"
        />

        <h5 v-if="fileSupport">{{ fileSupport }}</h5>

        <h5 v-if="downloadFailed" class="centered red">Download Failed</h5>
      </q-card-section>

      <q-card-actions style="justify-content: center;">
        <q-select
          outlined
          label="File Versions"
          v-model="selectedFileOption"
          :options="fileOptions"
          option-disable="inactive"
          style="width: 500px; margin-right: 50px"
          v-if="fileOptions.length > 0"
        />
        <q-btn
          @click="downloadDiffFormat()"
          icon="download"
          label="Download"
          text-color="white"
          style="background: #0067c1"
          no-caps
          v-if="!fileLoading && !downloadFailed && !noFile"
        >
          <a ref="testBind" :href="anchorURL" :download="modalTitle"></a>
        </q-btn>
        <q-btn
          @click="shareFile()"
          label="Share"
          text-color="white"
          style="background: #0067c1"
          no-caps
          v-if="showShareButton && !fileLoading && !downloadFailed && !noFile"
        />
        <q-btn
          label="Close"
          text-color="white"
          style="background: #0067c1"
          no-caps
          v-close-popup
        />
        <q-pagination
          v-model="currentDocumentIdIndex"
          :max="maxPagination"
          input
          style="position: absolute; right: 2%;"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import { computed, ref, watch } from "vue";
import APIKeyService from "../service/APIKeyService.js";
import MayanService from "../service/MayanService";
import CommonHelper from "../helper/CommonHelper.js";
import NotificationHelper from "../helper/NotificationHelper";
import EmailService from "../service/EmailService.js";
import axios from "axios";
import mammoth from "mammoth";
import moment from "moment";
import Tiff from "tiff.js";
import * as XLSX from "xlsx";

export default {
  props: {
    sideBarMode: String,
    resultTable: Object,
    foldersTable: Object,
    darkMode: Boolean
  },
  setup(props) {
    const previewDialog = ref(false);
    const currentDocumentId = ref("");
    const modalTitle = ref("");
    const fileLoading = ref(false);
    const downloadFailed = ref(false);
    const noFile = ref(false);
    const fileSupport = ref("");

    const showShareButton = computed(() => {
      return window.origin === process.env.VUE_APP_URL;
    });

    const previewSourceURL = ref("");
    const clientHeight = ref(null);
    const heightModal = ref("");
    const styleModal = ref();
    const anchorURL = ref("");

    const currentDocumentIdIndex = computed({
      get() {
        var returnValue = 0;

        if (previewDialog.value) {
          if (props.sideBarMode === "folders") {
            for (let i = 0; i < props.foldersTable.filePreviewRowData.length; i++) {
              if (props.foldersTable.filePreviewRowData[i].record.id === currentDocumentId.value) {
                returnValue = i + 1;
              }
            }
          } else {
            for (let i = 0; i < props.resultTable.resultsGrid.api.getDisplayedRowCount(); i++) {
              if (props.resultTable.resultsGrid.api.getDisplayedRowAtIndex(i).data.file_info[0] === currentDocumentId.value) {
                returnValue = i + 1;
                break;
              }
            }
          }
        }
  
        return returnValue;
      },
      set(newPage) {
        if (previewDialog.value) {
          if (props.sideBarMode === "folders") {
            downloadFile(props.foldersTable.filePreviewRowData[newPage - 1].record.id, props.foldersTable.filePreviewRowData[newPage - 1].record.label, true);
          } else {
            const latestDocumentId = props.resultTable.resultsGrid.api.getDisplayedRowAtIndex(newPage - 1).data.file_info;
            downloadFile(latestDocumentId[0], latestDocumentId[1], true);
          }
        }
  
        return newPage;     
      }
    });

    const maxPagination = computed(() => {
      var returnValue = null;
      if (previewDialog.value) {
        if (props.sideBarMode === "folders") {
          returnValue = props.foldersTable.filePreviewRowData.length;
        } else {
          returnValue = props.resultTable.resultsGrid.api.getDisplayedRowCount();
        }
      }
      return returnValue;
    });

    const isExcel = ref(false);
    const workBook = ref();
    const sheetListArray = ref([]);

    const isDocx = ref(false);
    const docxHTML = ref();
    const docTextColor = ref("black");

    const storedBlob = ref({});
    const storedMimetype = ref({});
    const storedWorkBook = ref({});
    const storedDocxHTML = ref({});
    const storedTiff = ref({});

    const fileOptions = computed(() => {
      let returnValue = [];

      if (currentDocumentId.value) {
        const fileLatest = CommonHelper.storedFileLatest.value[currentDocumentId.value];

        if (fileLatest && fileLatest.length > 1) {
          let emphasizeFiles = [];
          let files = [];

          const emphasizeFileId = CommonHelper.currentFileVersionData.value.emphasizeFileId ? CommonHelper.currentFileVersionData.value.emphasizeFileId : [];
          const hiddenFileId = CommonHelper.currentFileVersionData.value.hiddenFileId ? CommonHelper.currentFileVersionData.value.hiddenFileId : [];

          fileLatest.forEach((file) => {
            if (!hiddenFileId.includes(file.id)) {
              if (emphasizeFileId.includes(file.id)) {
                emphasizeFiles.push({
                  value: file,
                  label: getFileOptionLabel(file, true)
                });
              } else {
                files.push({
                  value: file,
                  label: getFileOptionLabel(file, true)
                });
              }
            }
          })

          if (emphasizeFiles.length > 0 && files.length > 0) {
            returnValue.push({
              label: "Emphasized Version",
              inactive: true
            });

            returnValue = returnValue.concat(emphasizeFiles);

            returnValue.push({
              label: "Versions",
              inactive: true
            });

            returnValue = returnValue.concat(files);
          } else {
            returnValue = emphasizeFiles.concat(files);
          }
        }
      }

      return returnValue;
    });

    function getFileOptionLabel(file, includeTimestamp) {
      let returnValue = file.filename;

      if (returnValue.includes(":::")) {
        returnValue = returnValue.split(":::")[1];
      }

      if (includeTimestamp) {
        returnValue = `${returnValue} (${moment(file.timestamp).format("MM-DD-YYYY hh:mm a" )})`;
      }

      return returnValue;
    }

    const selectedFileOption = ref(null);

    watch(selectedFileOption, () => {
      if (selectedFileOption.value && selectedFileOption.value.value) {
        const file = selectedFileOption.value.value;

        const fileUrl = file.url;
        const originalFilename = getFileOptionLabel(file, false);
        const mimetype = getMimetype(file.mimetype, originalFilename);
        const documentId = file.document_id.toString();

        modalTitle.value = originalFilename;
        previewSourceURL.value = ``;
        isExcel.value = false;
        fileSupport.value = ``;
        downloadFailed.value = false;
        noFile.value = false;
        fileLoading.value = true;
        heightModal.value = `300px`;
        currentDocumentId.value = documentId;
        styleModal.value = "";
        isDocx.value = false;
        previewDialog.value = true;

        if (storedBlob.value[fileUrl] != undefined) {
          displayStoreDownloadedFile(fileUrl, mimetype, originalFilename, null, null, true);
        } else {
          MayanService.downloadUrl(fileUrl)
          .then((downloadUrlResponse) => {
            const downloadUrl = downloadUrlResponse.data.data;

            APIKeyService.generateApiToken().then((response) => {
              const uid = response.documentName;
              const apiKey = response.apiKey;

              axios
              .get(
                `${process.env.VUE_APP_FASTIFY}downloadFromMayanStorage?downloadURL=${encodeURIComponent(downloadUrl)}&apiKey=${apiKey}&uid=${uid}`,
                {
                  responseType: "arraybuffer",
                }
              )
              .then((response) => {
                displayStoreDownloadedFile(fileUrl, mimetype, originalFilename, response, null, true);
              })
              .catch((error) => {
                const errorMessage = `documentId: ${documentId}
fileUrl: ${fileUrl}

${error}`;

                EmailService.sendErrorEmail("FilePreview.vue - watch - selectedFileOption", errorMessage);
                downloadFileFailed(documentId, originalFilename);
              });
            })
          })
          .catch((error) => {
            const errorMessage = `documentId: ${documentId}
fileUrl: ${fileUrl}

${error}`;

            EmailService.sendErrorEmail("FilePreview.vue - watch - selectedFileOption", errorMessage);
            downloadFileFailed(documentId, originalFilename);
          });
        }
      }
    });

    /*
     * Downloads the file, then calls saveZipFileData
     */
    function downloadFileSaveZipFileData(documentId, fileUrl) {
      MayanService.downloadUrl(fileUrl)
      .then((downloadUrlResponse) => {
        const downloadUrl = downloadUrlResponse.data.data;

        APIKeyService.generateApiToken().then((response) => {
          const uid = response.documentName;
          const apiKey = response.apiKey;

          axios
          .get(
            `${process.env.VUE_APP_FASTIFY}downloadFromMayanStorage?downloadURL=${encodeURIComponent(downloadUrl)}&apiKey=${apiKey}&uid=${uid}`,
            {
              responseType: "arraybuffer",
            }
          )
          .then((response) => {
            displayStoreDownloadedFile(documentId, "application/zip", "", response, null, false);
            CommonHelper.saveZipFileData(documentId, storedBlob.value[documentId]);
          })
          .catch((error) => {
            const errorMessage = `documentId: ${documentId}
fileUrl: ${fileUrl}

${error}`;

            EmailService.sendErrorEmail("FilePreview.vue - downloadFileSaveZipFileData", errorMessage);
          });
        })
      })
      .catch((error) => {
        const errorMessage = `documentId: ${documentId}
fileUrl: ${fileUrl}

${error}`;

        EmailService.sendErrorEmail("FilePreview.vue - downloadFileSaveZipFileData", errorMessage);
      });
    }

    function downloadFileOrUpdateSelectedFileOption(documentIdFileUrl, originalFilename) {
      if (documentIdFileUrl.startsWith("http")) {
        selectedFileOption.value = null;

        Object.values(CommonHelper.storedFileLatest.value).forEach((fileLatest) => {
          fileLatest.forEach((file) => {
            if (file.url === documentIdFileUrl) {
              selectedFileOption.value = {
                value: file,
                label: getFileOptionLabel(file, true)
              };
            }
          })
        })
      } else {
        downloadFile(documentIdFileUrl, originalFilename, true);
      }
    }

    /*
     * Downloads the file
     */
    function downloadFile(documentId, originalFilename, isFilePreview) {
      if (isFilePreview) {
        modalTitle.value = originalFilename;
        previewSourceURL.value = ``;
        isExcel.value = false;
        fileSupport.value = ``;
        downloadFailed.value = false;
        noFile.value = false;
        fileLoading.value = true;
        heightModal.value = `300px`;
        currentDocumentId.value = documentId;
        styleModal.value = "";
        isDocx.value = false;
        previewDialog.value = true;
        selectedFileOption.value = null;
      }

      if (storedBlob.value[documentId] != undefined) {
        displayStoreDownloadedFile(documentId, storedMimetype.value[documentId], originalFilename, null, null, isFilePreview);

        updateStoredFileLatest(documentId);
      } else {
        updateStoredFileLatest(documentId)
        .then(() => {
          const results = CommonHelper.storedFileLatest.value[documentId];

          if (!results) {
            if (isFilePreview) {
              downloadFileFailed(documentId, originalFilename);
            }
          } else if (results.length === 0) {
            if (isFilePreview) {
              displayNoFile(documentId);
            }
          } else {
            const fileUrl = results[0].url;
            const mimetype = getMimetype(results[0].mimetype, results[0].filename);

            storedMimetype.value[documentId] = mimetype;

            MayanService.downloadUrl(fileUrl)
            .then((downloadUrlResponse) => {
              const downloadUrl = downloadUrlResponse.data.data;

              APIKeyService.generateApiToken().then((response) => {
                const uid = response.documentName;
                const apiKey = response.apiKey;

                axios
                .get(
                  `${process.env.VUE_APP_FASTIFY}downloadFromMayanStorage?downloadURL=${encodeURIComponent(downloadUrl)}&apiKey=${apiKey}&uid=${uid}`,
                  {
                    responseType: "arraybuffer",
                  }
                )
                .then((response) => {
                  const additionalFileUrl = results.length > 1 ? fileUrl: null;
                  displayStoreDownloadedFile(documentId, mimetype, originalFilename, response, additionalFileUrl, isFilePreview);
                })
                .catch((error) => {
                  const errorMessage = `documentId: ${documentId}
fileUrl: ${fileUrl}

${error}`;

                  EmailService.sendErrorEmail("FilePreview.vue - downloadFile", errorMessage);
                  if (isFilePreview) {
                    downloadFileFailed(documentId, originalFilename);
                  }
                });
              })
            })
            .catch((error) => {
              const errorMessage = `documentId: ${documentId}
fileUrl: ${fileUrl}

${error}`;

              EmailService.sendErrorEmail("FilePreview.vue - downloadFile", errorMessage);
              if (isFilePreview) {
                downloadFileFailed(documentId, originalFilename);
              }
            });
          }
        })
        .catch((error) => {
          const errorMessage = `documentId: ${documentId}

${error}`;

          EmailService.sendErrorEmail("FilePreview.vue - downloadFile", errorMessage);
          if (isFilePreview) {
            downloadFileFailed(documentId, originalFilename);
          }
        });
      }
    }

    function displayNoFile(documentId) {
      if (displayDownloadedFile(documentId)) {
        styleModal.value = "display: none";
        fileLoading.value = false;
        noFile.value = true;
        fileSupport.value = `File is still being processed after upload. Please try again later.`;
      } else {
        NotificationHelper.createInfoNotification("File is still being processed after upload. Please try again later.");
      }
    }

    /*
     * Retrieves mimetype from fileLatest (or returns mimetype based on the filename)
     */
    function getMimetype(mimetype, filename) {
      let returnValue = mimetype;

      if (returnValue === null) {
        const extensionIndex = filename.lastIndexOf('.');

        if (extensionIndex > 0) {
          const extension = filename.substr(extensionIndex + 1);

          switch (extension) {
            case "docx":
              returnValue = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
              break;
            case "jpeg":
              returnValue = "image/jpeg";
              break;
            case "jpg":
              returnValue = "image/jpeg";
              break;
            case "pdf":
              returnValue = "application/pdf";
              break;
            case "png":
              returnValue = "image/png";
              break;
            case "tif":
              returnValue = "image/tiff";
              break;
            case "tiff":
              returnValue = "image/tiff";
              break;
            case "txt":
              returnValue = "text/plain";
              break;
            case "xls":
              returnValue = "application/vnd.ms-excel";
              break;
            case "xlsx":
              returnValue = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
              break;
          }
        }
      }

      return returnValue;
    }

    /*
     * Displays and/or stores the downloaded file
     */
    function displayStoreDownloadedFile(documentIdFileUrl, mimetype, originalFilename, response, additionalFileUrl, isFilePreview) {
      let currentBlob = storedBlob.value[documentIdFileUrl];

      if (currentBlob == undefined) {
        const fileArrayBuffer = response.data;
        const totalFileSize = response.data.byteLength;
        let blobParts = [];
        let chunkSize = 500000000; // 500 MB = 500,000,000 bytes
        let index = 0;

        while (index < totalFileSize) {
          if (totalFileSize - index < chunkSize) {
            chunkSize = totalFileSize - index;
          }
          let chunkedBuffer = fileArrayBuffer.slice(index, index + chunkSize);
          blobParts.push(chunkedBuffer);
          index += chunkSize;
        }
        currentBlob = new Blob(blobParts, { type: mimetype });
        storedBlob.value[documentIdFileUrl] = currentBlob;

        if (additionalFileUrl) {
          storedBlob.value[additionalFileUrl] = currentBlob;
        }
      }

      if (isFilePreview) {
        let url = null;

        if (displayDownloadedFile(documentIdFileUrl)) {
          url = window.URL.createObjectURL(currentBlob);
          anchorURL.value = `${url}`;

          heightModal.value = getHeightModal(mimetype);
        }

        if (
          mimetype == "application/pdf" ||
          mimetype == "image/png" ||
          mimetype == "image/jpeg"
        ) {
          displayPdfPngJpegFile(documentIdFileUrl, url);
        } else if (
          mimetype == "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
          mimetype == "application/vnd.ms-excel" ||
          mimetype == "text/plain" ||
          mimetype == "text/*"
        ) {
          displayStoreExcelFile(documentIdFileUrl, response, additionalFileUrl);
        } else if (
          mimetype == "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ) {
          displayStoreWordFile(documentIdFileUrl, response, additionalFileUrl);
        } else if (
          mimetype == "image/tiff" &&
          !navigator.userAgent.includes("Firefox")
        ) {
          displayStoreTiffFile(documentIdFileUrl, response, additionalFileUrl);
        } else {
          displayOtherFile(documentIdFileUrl);
        }

        if (displayDownloadedFile(documentIdFileUrl)) {
          fileLoading.value = false;
        } else {
          // display download succeeded notification
          NotificationHelper.createNotification(
            `Download of "${originalFilename}" succeeded`,
            {
              timeout: 0,
              type: "positive",
              actions: [
                { label: 'Display', color: 'white', handler: () => { downloadFileOrUpdateSelectedFileOption(documentIdFileUrl, originalFilename) } },
                { label: 'Dismiss', color: 'white' }
              ]
            }
          );
        }
      }
    }

    /*
     * Removes a document id key from storedBlob and other stored refs
     */
    function removeStoredBlobDocumentId(documentId) {
      if (storedBlob.value[documentId] !== undefined) {
        delete storedBlob.value[documentId];
      }

      if (storedMimetype.value[documentId] !== undefined) {
        delete storedMimetype.value[documentId];
      }

      if (storedWorkBook.value[documentId] !== undefined) {
        delete storedWorkBook.value[documentId];
      }

      if (storedDocxHTML.value[documentId] !== undefined) {
        delete storedDocxHTML.value[documentId];
      }

      if (storedTiff.value[documentId] !== undefined) {
        delete storedTiff.value[documentId];
      }
    }

    async function storeBlobDocumentIdDuringUpload(documentId, blob, mimetype) {
      storedBlob.value[documentId] = blob;

      storedMimetype.value[documentId] = mimetype;

      if (
        mimetype == "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        mimetype == "application/vnd.ms-excel" ||
        mimetype == "text/plain" ||
        mimetype == "text/*"
      ) {
        const arrayBuffer = await blob.arrayBuffer();

        storedWorkBook.value[documentId] = XLSX.read(
          arrayBuffer,
          { type: "array" }
        );
      } else if (
        mimetype == "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ) {
        const arrayBuffer = await blob.arrayBuffer();

        mammoth
          .convertToHtml({ arrayBuffer: arrayBuffer })
          .then(function(result) {
            storedDocxHTML.value[documentId] = result.value; // The generated HTML
          })
          .done();
      } else if (
        mimetype == "image/tiff" &&
        !navigator.userAgent.includes("Firefox")
      ) {
        const arrayBuffer = await blob.arrayBuffer();

        storedTiff.value[documentId] = new Tiff({ buffer: arrayBuffer });
      }
    }

    /*
     * Displays download failed
     */
    async function downloadFileFailed(documentId, originalFilename) {
      let documentFound = false;

      await MayanService.documentIdSearch(documentId)
        .then((response) => {
          documentFound = response.data && response.data.id;
        })
        .catch((error) => {
          const errorMessage = `documentId: ${documentId}

${error}`;

          EmailService.sendErrorEmail("FilePreview.vue - downloadFileFailed", errorMessage);
          NotificationHelper.createErrorNotification("Error encountered. Please try again.");
        });

      if (documentFound) {
        updateStoredFileLatest(documentId);

        if (displayDownloadedFile(documentId)) {
          // display download failed in preview modal
          styleModal.value = "display: none";
          fileLoading.value = false;
          downloadFailed.value = true;
        } else {
          // display download failed notification
          NotificationHelper.createErrorNotification(`Download of "${originalFilename}" failed`);
        }
      } else {
        // display document doesn't exist notification
        previewDialog.value = false;
        CommonHelper.deleteFromTableInput(documentId);
        NotificationHelper.createErrorNotification(`"${originalFilename}" doesn't exist and has been removed from results`);
      }
    }

    async function updateStoredFileLatest(documentId) {
      await CommonHelper.updateStoredFileLatest(documentId, true);

      if (selectedFileOption.value && !fileOptions.value.includes(selectedFileOption.value)) {
        selectedFileOption.value = null;
      }
    }

    /*
     * Returns the height modal that corresponds to the mimetype
     */
    function getHeightModal(mimetype) {
      var clientHeightValue = clientHeight.value;

      if (clientHeightValue === null) {
        let previewCard = document.getElementById('previewCard');

        if (previewCard) {
          clientHeightValue = previewCard.clientHeight;
          clientHeight.value = clientHeightValue;
        } else {
          // previewCard isn't being displayed yet so use a default value instead
          clientHeightValue = 950;
        }
      }

      if (
          mimetype == "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
          mimetype == "application/vnd.ms-excel" ||
          mimetype == "text/plain" ||
          mimetype == "text/*"
      ) {
        return 0.76 * clientHeightValue;
      } else {
        return 0.825 * clientHeightValue;
      }
    }

    /*
     * Displays the pdf, png, or jpeg file
     */
    function displayPdfPngJpegFile(documentIdFileUrl, url) {
      if (displayDownloadedFile(documentIdFileUrl)) {
        previewSourceURL.value = `${url}#zoom=100`;
      }
    }

    /*
     * Displays and/or stores the Excel file
     */
    function displayStoreExcelFile(documentIdFileUrl, response, additionalFileUrl) {
      let currentWorkBook = storedWorkBook.value[documentIdFileUrl];

      if (currentWorkBook == undefined) {
        currentWorkBook = XLSX.read(
          response.data,
          { type: "array" }
        );
        storedWorkBook.value[documentIdFileUrl] = currentWorkBook;

        if (additionalFileUrl) {
          storedWorkBook.value[additionalFileUrl] = currentWorkBook;
        }
      }

      if (displayDownloadedFile(documentIdFileUrl)) {
        isExcel.value = true;
        workBook.value = currentWorkBook;
        sheetListArray.value = workBook.value.SheetNames;
        generateTableFromSheet(sheetListArray.value[0]);
      }
    }

    /*
     * Displays and/or stores the Word file
     */
    function displayStoreWordFile(documentIdFileUrl, response, additionalFileUrl) {
      let currentDocxHTML = storedDocxHTML.value[documentIdFileUrl];

      if (currentDocxHTML == undefined) {
        mammoth
          .convertToHtml({ arrayBuffer: response.data })
          .then(function(result) {
            currentDocxHTML = result.value; // The generated HTML
            storedDocxHTML.value[documentIdFileUrl] = currentDocxHTML;

            if (additionalFileUrl) {
              storedDocxHTML.value[additionalFileUrl] = currentDocxHTML;
            }

            if (displayDownloadedFile(documentIdFileUrl)) {
              docxHTML.value = currentDocxHTML;
              isDocx.value = true;
              generateDocxIframe();
            }
          })
          .done();
      } else {
        if (displayDownloadedFile(documentIdFileUrl)) {
          docxHTML.value = currentDocxHTML;
          isDocx.value = true;
          generateDocxIframe();
        }
      }
    }

    /*
     * Displays and/or stores the Tiff file
     */
    function displayStoreTiffFile(documentIdFileUrl, response, additionalFileUrl) {
      var currentTiff = storedTiff.value[documentIdFileUrl];

      if (currentTiff == undefined) {
        currentTiff = new Tiff({ buffer: response.data });
        storedTiff.value[documentIdFileUrl] = currentTiff;

        if (additionalFileUrl) {
          storedTiff.value[additionalFileUrl] = currentTiff;
        }
      }

      if (displayDownloadedFile(documentIdFileUrl)) {
        var doc = document.getElementsByTagName("iframe");
        setTimeout(() => {
          var doc1 = doc[0].contentWindow.document;

          // clear out body
          while (doc1.body.firstChild) {
            doc1.body.removeChild(doc1.body.firstChild);
          }

          // append tiff canvas to body
          for (
            var i = 0, len = currentTiff.countDirectory();
            i < len;
            ++i
          ) {
            currentTiff.setDirectory(i);
            var canvas = currentTiff.toCanvas();
            canvas.setAttribute(
              "style",
              "width:" +
                currentTiff.width() * 0.3 +
                "px; height: " +
                currentTiff.height() * 0.3 +
                "px"
            );
            doc1.body.append(canvas);
          }
        }, 1);
      }
    }

    /*
     * Displays the other file
     */
    function displayOtherFile(documentIdFileUrl) {
      if (displayDownloadedFile(documentIdFileUrl)) {
        styleModal.value = "display: none";
        fileSupport.value = `File is not supported for browser view. Please download it using the button below.`;
      }
    }

    /*
     * Returns whether or not to display the downloaded file that corresponds to documentIdFileUrl
     */
    function displayDownloadedFile(documentIdFileUrl) {
      let returnValue = false;

      if (previewDialog.value) {
        if (currentDocumentId.value === documentIdFileUrl) {
          returnValue = true;
        } else if (selectedFileOption.value && selectedFileOption.value.value && selectedFileOption.value.value.url === documentIdFileUrl) {
          returnValue = true;
        }
      }

      return returnValue;
    }

    function generateTableFromSheet(sheetName) {
      let sheetData = XLSX.utils.sheet_to_json(
        workBook.value.Sheets[sheetName],
        { header: 1 }
      );

      if (sheetData.length > 0) {
        var table_output = '<div class="table-wrapper"><table>';

        for (var row = 0; row < sheetData.length; row++) {
          table_output += "<tr>";

          for (var cell = 0; cell < sheetData[row].length; cell++) {
            if (row == 0) {
              let cellValue =
                typeof sheetData[row][cell] == "undefined"
                  ? " "
                  : sheetData[row][cell];
              table_output += "<th>" + cellValue + "</th>";
            } else {
              let cellValue =
                typeof sheetData[row][cell] == "undefined"
                  ? " "
                  : sheetData[row][cell];
              table_output += "<td>" + cellValue + "</td>";
            }
          }

          table_output += "</tr>";
        }

        table_output += "</table> </div>";

        var doc = document.getElementsByTagName("iframe");

        var css =
          "" +
          '<style type="text/css">' +
          `html,body {
            padding: 0;
            height: 100%;
          }

          body {
            display: flex;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            font-size: 60%;
            color: #333;
            justify-content: center;
          }

          .table-wrapper {
            overflow: scroll;
          }

          table {
            position: relative;
            border: 1px solid #ddd;
            border-collapse: collapse;
            background-color: #eee;
          }

          td, th {
            white-space: nowrap;
            border: 1px solid #ddd;
            padding: 20px;
          }

          th {
            background-color: #eee;
          }

          tbody tr td:first-of-type {
            background-color: #eee;
          }` +
          "</style>";
        setTimeout(() => {
          var doc1 = doc[0].contentWindow.document;
          doc1.open();
          doc1.write(table_output + css);
          doc1.close();
        }, 100);
      }
    }

    function generateDocxIframe() {
      var doc = document.getElementsByTagName("iframe");
      setTimeout(() => {
        var doc1 = doc[0].contentWindow.document;
        var css = `<style>
        img {
          max-height: 400px;
          max-width: 300px;
        }
        html{
          color: ${docTextColor.value}
        }
        </style>`;
        doc1.open();
        doc1.write(docxHTML.value + css);
        doc1.close();
      }, 1);
    }

    function shareFile() {
      navigator.clipboard.writeText(`${process.env.VUE_APP_URL}?document_id=${currentDocumentId.value}`);
      NotificationHelper.createInfoNotification("Link copied to clipboard");
    }

    watch(
      () => props.darkMode,
      () => {
        if (props.darkMode) {
          document.documentElement.style.setProperty(
            "--resultsModalBackground",
            "#4A4A4A"
          );
          document.documentElement.style.setProperty(
            "--resultsModalText",
            "white"
          );
          docTextColor.value = "white";
          if (isDocx.value) {
            generateDocxIframe();
          }
        } else {
          document.documentElement.style.setProperty(
            "--resultsModalBackground",
            "white"
          );
          document.documentElement.style.setProperty(
            "--resultsModalText",
            "black"
          );
          docTextColor.value = "black";
          if (isDocx.value) {
            generateDocxIframe();
          }
        }
      },
      { deep: true, immediate: true }
    );

    return {
      //variable properties
      previewDialog,
      currentDocumentId,
      modalTitle,
      fileLoading,
      downloadFailed,
      noFile,
      fileSupport,
      showShareButton,
      previewSourceURL,
      heightModal,
      styleModal,
      anchorURL,
      isExcel,
      sheetListArray,
      currentDocumentIdIndex,
      maxPagination,
      fileOptions,
      selectedFileOption,
      storedBlob,

      //functions
      downloadFileSaveZipFileData,
      downloadFile,
      removeStoredBlobDocumentId,
      storeBlobDocumentIdDuringUpload,
      generateTableFromSheet,
      shareFile
    };
  },
  methods: {
    downloadDiffFormat() {
      this.$refs.testBind.click();
    },
  }
};
</script>